<template>
  <div class="volume">
    <button @click="handleIconClick" @mouseenter="isHovered = true" @mouseleave="isHovered = false">
      <VolumeFull v-if="volume === 1" :colorMain="defineMainColor" />
      <VolumeHigh v-else-if="volume >= 0.7 && volume < 1" :colorMain="defineMainColor" :colorSecondary="defineSecondaryColor" />
      <VolumeHalf v-else-if="volume >= 0.35 && volume < 0.7" :colorMain="defineMainColor" :colorSecondary="defineSecondaryColor" />
      <VolumeLow v-else-if="volume > 0 && volume < 0.35" :colorMain="defineMainColor" :colorSecondary="defineSecondaryColor" />
      <VolumeOff v-else-if="volume === 0" :colorMain="defineMainColor" />
    </button>
    <div class="volume__bar" ref="bar" @mousedown="startDrag" @touchstart="startDrag" v-if="!props.isHideBar">
      <div class="volume__progress" :style="{ width: `${volume * 100}%` }">
        <div class="volume__thumb" :class="{ 'volume__thumb--active': isDragging && interactionType === 'touch' }"></div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { Props } from './props';
  import { useStore } from 'vuex';
  import { ref, onBeforeUnmount, computed } from 'vue';
  //--------------------------------
  import VolumeFull from './icons/VolumeFull.vue';
  import VolumeHigh from './icons/VolumeHigh.vue';
  import VolumeHalf from './icons/VolumeHalf.vue';
  import VolumeLow from './icons/VolumeLow.vue';
  import VolumeOff from './icons/VolumeOff.vue';
  //--------------------------------
  const store = useStore();
  const props = defineProps(Props());
  //--------------------------------
  const bar = ref(null);
  const isDragging = ref(false);
  const isHovered = ref(false);
  const interactionType = ref(null);
  //--------------------------------
  //Очищаем обработчики при размонтировании компонента
  onBeforeUnmount(() => {
    removeEventListeners();
  });
  //--------------------------------
  //Получам значение из store для громкости
  const volume = computed(() => store.state.Audioplayer.volume);
  //Определяем главный цвет для иконки при ховере
  const defineMainColor = computed(() => isHovered.value ? 'var(--link)' : 'var(--color-secondary-middle)');
  //Определяем второстепенный цвет для иконки при ховере
  const defineSecondaryColor = computed(() => isHovered.value ? 'var(--link-silent)' : 'var(--color-secondary-light)');
  //--------------------------------
  //Циклическое изменение громкости при клике на иконку
  const handleIconClick = () => {
    if (volume.value === 1) {
      changeVolume(0.7);
    } else if (volume.value >= 0.7 && volume.value < 1) {
      changeVolume(0.5);
    } else if (volume.value >= 0.35 && volume.value < 0.7) {
      changeVolume(0.25);
    } else if (volume.value > 0 && volume.value < 0.35) {
      changeVolume(0);
    } else if (volume.value === 0) {
      changeVolume(1);
    }
  };
  
  //Изменение громкости
  const changeVolume = (newVolume) => {
    store.dispatch('Audioplayer/changeVolume', Math.max(0, Math.min(1, newVolume)));
  };
  
  //Обработчик начала перетаскивания
  const startDrag = (e) => {
    isDragging.value = true;
    interactionType.value = e.type === 'touchstart' ? 'touch' : 'mouse';
    if (e.type === 'touchstart') {
      e.preventDefault();
    }
    updateVolume(e);
    addEventListeners();
  };
  
  //Добавляет глобальные обработчики для отслеживания перемещения
  const addEventListeners = () => {
    window.addEventListener('mousemove', handleDrag);
    window.addEventListener('touchmove', handleDrag, { passive: false });
    window.addEventListener('mouseup', stopDrag);
    window.addEventListener('touchend', stopDrag);
  };
  
  //Удаляет глобальные обработчики событий
  const removeEventListeners = () => {
    window.removeEventListener('mousemove', handleDrag);
    window.removeEventListener('touchmove', handleDrag);
    window.removeEventListener('mouseup', stopDrag);
    window.removeEventListener('touchend', stopDrag);
  };
  
  //Обработчик перемещения при перетаскивании
  const handleDrag = (e) => {
    if (!isDragging.value) return;
    if (e.type === 'touchmove') {
      e.preventDefault();
    }
    updateVolume(e);
  };
  
  //Обработчик окончания перетаскивания
  const stopDrag = () => {
    isDragging.value = false;
    removeEventListeners();
  };
  
  //Обновляет значение громкости на основе позиции курсора
  const updateVolume = (e) => {
    if (!bar.value) return;
    const rect = bar.value.getBoundingClientRect();
    const clientX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX;
    let pos = (clientX - rect.left) / rect.width;
    pos = Math.max(0, Math.min(1, pos)); 
    changeVolume(pos);
  };
</script>

<style scoped lang="scss">
  @import './styles.scss';
</style>